import JSZip from "jszip";
import { extractAudioFeatures, type AudioFeatureReport } from "./audioFeatures";
import {
  getAllAudioLabTestCases,
  getAudioLabInstrumentById,
  getAudioLabInstruments,
  getAudioLabTestCases,
  sanitizeFilenamePart,
  type AudioLabTestCase
} from "./audioLabRegistry";
import {
  AI_REVIEW_PROMPT,
  createAudioLabManifest,
  createFeatureIndex,
  createGlobalPresetExport,
  createInstrumentPresetExport
} from "./manifest";
import { createSpectrogramPngBlob } from "./spectrogram";
import { encodeAudioBufferToWavBlob, renderTestCaseToAudioBuffer } from "./wavExport";

export type AudioLabAssetKind = "wav" | "spectrogram" | "features";

export interface AudioLabFailure {
  type: "render_failed" | "spectrogram_failed" | "feature_failed" | "zip_failed";
  instrumentId: string;
  note?: string;
  articulation?: string;
  message: string;
}

export interface AudioLabProgressSnapshot {
  total: number;
  completed: number;
  current: string;
  phase: "rendering" | "compressing" | "complete";
}

export interface AudioLabZipOptions {
  signal?: AbortSignal;
  onProgress?: (progress: AudioLabProgressSnapshot) => void;
}

interface RenderedTestAssets {
  audioBuffer: AudioBuffer;
  wavBlob: Blob;
  spectrogramBlob: Blob;
  features: AudioFeatureReport;
  metadata: {
    instrumentId: string;
    instrumentName: string;
    note: string;
    articulation: string;
    durationSec: number;
    sampleRate: number;
    channels: number;
    filenameStem: string;
  };
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function ensureNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Audio Lab export was cancelled.", "AbortError");
  }
}

function jsonText(data: unknown): string {
  return JSON.stringify(data, null, 2);
}

function createFailure(
  type: AudioLabFailure["type"],
  testCase: Pick<AudioLabTestCase, "instrumentId" | "note" | "articulation">,
  error: unknown
): AudioLabFailure {
  return {
    type,
    instrumentId: testCase.instrumentId,
    note: testCase.note,
    articulation: testCase.articulation,
    message: toErrorMessage(error)
  };
}

function emitProgress(
  options: AudioLabZipOptions | undefined,
  total: number,
  completed: number,
  current: string,
  phase: AudioLabProgressSnapshot["phase"] = "rendering"
): void {
  options?.onProgress?.({
    total,
    completed,
    current,
    phase
  });
}

async function renderTestAssets(testCase: AudioLabTestCase, signal?: AbortSignal): Promise<RenderedTestAssets> {
  ensureNotAborted(signal);

  const audioBuffer = await renderTestCaseToAudioBuffer(testCase);
  const wavBlob = encodeAudioBufferToWavBlob({
    audioBuffer,
    channels: testCase.channels
  });
  const spectrogramBlob = await createSpectrogramPngBlob({ audioBuffer });
  const features = extractAudioFeatures({
    audioBuffer,
    instrumentId: testCase.instrumentId,
    note: testCase.note,
    articulation: testCase.articulation,
    durationSec: testCase.durationSec,
    sampleRate: testCase.sampleRate
  });

  return {
    audioBuffer,
    wavBlob,
    spectrogramBlob,
    features,
    metadata: {
      instrumentId: testCase.instrumentId,
      instrumentName: testCase.instrumentName,
      note: testCase.note,
      articulation: testCase.articulation,
      durationSec: testCase.durationSec,
      sampleRate: testCase.sampleRate,
      channels: testCase.channels,
      filenameStem: testCase.filenameStem
    }
  };
}

function addRenderedAssetsToInstrumentFolder(
  folder: JSZip,
  testCase: AudioLabTestCase,
  assets: RenderedTestAssets
): void {
  folder.file(`wav/${testCase.filenameStem}.wav`, assets.wavBlob);
  folder.file(`spectrogram/${testCase.filenameStem}.spectrogram.png`, assets.spectrogramBlob);
  folder.file(`features/${testCase.filenameStem}.features.json`, jsonText(assets.features));
}

function addRenderedAssetsToBundle(folder: JSZip, assets: RenderedTestAssets): void {
  folder.file("audio.wav", assets.wavBlob);
  folder.file("spectrogram.png", assets.spectrogramBlob);
  folder.file("features.json", jsonText(assets.features));
  folder.file("metadata.json", jsonText(assets.metadata));
}

async function generateZipBlob(zip: JSZip, options?: AudioLabZipOptions): Promise<Blob> {
  emitProgress(options, 1, 0, "compressing zip", "compressing");

  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });

  emitProgress(options, 1, 1, "zip complete", "complete");

  return blob;
}

export async function buildTestBundleZip(testCase: AudioLabTestCase, options?: AudioLabZipOptions): Promise<Blob> {
  ensureNotAborted(options?.signal);

  const zip = new JSZip();
  const bundle = zip.folder(sanitizeFilenamePart(testCase.filenameStem));
  const instrument = getAudioLabInstrumentById(testCase.instrumentId);

  if (!bundle) {
    throw new Error("Failed to create test bundle folder.");
  }

  emitProgress(options, 4, 0, `${testCase.filenameStem}.wav`);
  const assets = await renderTestAssets(testCase, options?.signal);

  addRenderedAssetsToBundle(bundle, assets);
  bundle.file("preset.json", jsonText(createInstrumentPresetExport(instrument)));
  emitProgress(options, 4, 4, `${testCase.filenameStem}.zip`);

  return generateZipBlob(zip, options);
}

export async function buildInstrumentFeatureIndex(
  instrumentId: string,
  options?: AudioLabZipOptions
): Promise<ReturnType<typeof createFeatureIndex>> {
  const reports: AudioFeatureReport[] = [];
  const failures: AudioLabFailure[] = [];
  const testCases = getAudioLabTestCases(instrumentId);
  let completed = 0;

  for (const testCase of testCases) {
    ensureNotAborted(options?.signal);
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.features.json`);

    try {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);

      reports.push(
        extractAudioFeatures({
          audioBuffer,
          instrumentId: testCase.instrumentId,
          note: testCase.note,
          articulation: testCase.articulation,
          durationSec: testCase.durationSec,
          sampleRate: testCase.sampleRate
        })
      );
    } catch (error) {
      failures.push(createFailure("feature_failed", testCase, error));
    }

    completed += 1;
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.features.json`);
  }

  return createFeatureIndex(reports, failures);
}

export async function buildGlobalFeatureIndex(
  options?: AudioLabZipOptions
): Promise<ReturnType<typeof createFeatureIndex>> {
  const reports: AudioFeatureReport[] = [];
  const failures: AudioLabFailure[] = [];
  const testCases = getAllAudioLabTestCases();
  let completed = 0;

  for (const testCase of testCases) {
    ensureNotAborted(options?.signal);
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.features.json`);

    try {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);

      reports.push(
        extractAudioFeatures({
          audioBuffer,
          instrumentId: testCase.instrumentId,
          note: testCase.note,
          articulation: testCase.articulation,
          durationSec: testCase.durationSec,
          sampleRate: testCase.sampleRate
        })
      );
    } catch (error) {
      failures.push(createFailure("feature_failed", testCase, error));
    }

    completed += 1;
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.features.json`);
  }

  return createFeatureIndex(reports, failures);
}

export async function buildInstrumentAssetZip(
  instrumentId: string,
  assetKind: AudioLabAssetKind,
  options?: AudioLabZipOptions
): Promise<Blob> {
  const zip = new JSZip();
  const instrument = getAudioLabInstrumentById(instrumentId);
  const folder = zip.folder(`${sanitizeFilenamePart(instrument.id)}-${assetKind}`);
  const failures: AudioLabFailure[] = [];
  const testCases = getAudioLabTestCases(instrumentId);
  let completed = 0;

  if (!folder) {
    throw new Error("Failed to create instrument asset folder.");
  }

  for (const testCase of testCases) {
    ensureNotAborted(options?.signal);
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.${assetKind}`);

    try {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);

      if (assetKind === "wav") {
        folder.file(
          `${testCase.filenameStem}.wav`,
          encodeAudioBufferToWavBlob({ audioBuffer, channels: testCase.channels })
        );
      }

      if (assetKind === "spectrogram") {
        folder.file(`${testCase.filenameStem}.spectrogram.png`, await createSpectrogramPngBlob({ audioBuffer }));
      }

      if (assetKind === "features") {
        folder.file(
          `${testCase.filenameStem}.features.json`,
          jsonText(
            extractAudioFeatures({
              audioBuffer,
              instrumentId: testCase.instrumentId,
              note: testCase.note,
              articulation: testCase.articulation,
              durationSec: testCase.durationSec,
              sampleRate: testCase.sampleRate
            })
          )
        );
      }
    } catch (error) {
      failures.push(createFailure(assetKind === "spectrogram" ? "spectrogram_failed" : "render_failed", testCase, error));
    }

    completed += 1;
    emitProgress(options, testCases.length, completed, `${testCase.filenameStem}.${assetKind}`);
  }

  folder.file("failed.json", jsonText(failures));

  return generateZipBlob(zip, options);
}

export async function buildInstrumentAuditZip(instrumentId: string, options?: AudioLabZipOptions): Promise<Blob> {
  const zip = new JSZip();
  const instrument = getAudioLabInstrumentById(instrumentId);
  const root = zip.folder("audio-lab-export");
  const folder = root?.folder(`instruments/${sanitizeFilenamePart(instrument.id)}`);
  const failures: AudioLabFailure[] = [];
  const reports: AudioFeatureReport[] = [];
  const testCases = getAudioLabTestCases(instrumentId);
  const total = testCases.length * 3 + 3;
  let completed = 0;

  if (!root || !folder) {
    throw new Error("Failed to create instrument audit ZIP folders.");
  }

  folder.file("preset.json", jsonText(createInstrumentPresetExport(instrument)));
  completed += 1;
  emitProgress(options, total, completed, `${instrument.id}/preset.json`);

  for (const testCase of testCases) {
    ensureNotAborted(options?.signal);
    emitProgress(options, total, completed, `${testCase.filenameStem}.wav`);

    try {
      const assets = await renderTestAssets(testCase, options?.signal);

      addRenderedAssetsToInstrumentFolder(folder, testCase, assets);
      reports.push(assets.features);
      completed += 3;
      emitProgress(options, total, completed, `${testCase.filenameStem}.features.json`);
    } catch (error) {
      failures.push(createFailure("render_failed", testCase, error));
      completed += 3;
      emitProgress(options, total, completed, `${testCase.filenameStem} failed`);
    }
  }

  folder.file("feature-index.json", jsonText(createFeatureIndex(reports, failures)));
  root.file("manifest.json", jsonText(createAudioLabManifest(failures)));
  root.file("AI_REVIEW_PROMPT.md", AI_REVIEW_PROMPT);
  completed += 2;
  emitProgress(options, total, completed, `${instrument.id}/feature-index.json`);

  return generateZipBlob(zip, options);
}

export async function buildAllInstrumentsAuditZip(options?: AudioLabZipOptions): Promise<Blob> {
  const zip = new JSZip();
  const root = zip.folder("audio-lab-export");
  const instruments = getAudioLabInstruments();
  const failures: AudioLabFailure[] = [];
  const reports: AudioFeatureReport[] = [];
  const total = getAllAudioLabTestCases().length * 3 + instruments.length + 4;
  let completed = 0;

  if (!root) {
    throw new Error("Failed to create audio lab export root.");
  }

  root.file("global-presets.json", jsonText(createGlobalPresetExport()));
  completed += 1;
  emitProgress(options, total, completed, "global-presets.json");

  for (const instrument of instruments) {
    ensureNotAborted(options?.signal);

    const folder = root.folder(`instruments/${sanitizeFilenamePart(instrument.id)}`);

    if (!folder) {
      failures.push({
        type: "zip_failed",
        instrumentId: instrument.id,
        message: "Failed to create instrument folder."
      });
      continue;
    }

    folder.file("preset.json", jsonText(createInstrumentPresetExport(instrument)));
    completed += 1;
    emitProgress(options, total, completed, `${instrument.id}/preset.json`);

    for (const testCase of getAudioLabTestCases(instrument.id)) {
      ensureNotAborted(options?.signal);
      emitProgress(options, total, completed, `${testCase.filenameStem}.wav`);

      try {
        const assets = await renderTestAssets(testCase, options?.signal);

        addRenderedAssetsToInstrumentFolder(folder, testCase, assets);
        reports.push(assets.features);
        completed += 3;
        emitProgress(options, total, completed, `${testCase.filenameStem}.features.json`);
      } catch (error) {
        failures.push(createFailure("render_failed", testCase, error));
        completed += 3;
        emitProgress(options, total, completed, `${testCase.filenameStem} failed`);
      }
    }

    const instrumentReports = reports.filter((report) => report.instrumentId === instrument.id);
    const instrumentFailures = failures.filter((failure) => failure.instrumentId === instrument.id);

    folder.file("feature-index.json", jsonText(createFeatureIndex(instrumentReports, instrumentFailures)));
  }

  root.file("global-feature-index.json", jsonText(createFeatureIndex(reports, failures)));
  root.file("manifest.json", jsonText(createAudioLabManifest(failures)));
  root.file("AI_REVIEW_PROMPT.md", AI_REVIEW_PROMPT);
  completed += 3;
  emitProgress(options, total, completed, "manifest.json");

  return generateZipBlob(zip, options);
}
